import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, Clock, CheckCircle, XCircle, Mail } from 'lucide-react';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [recentTalents, setRecentTalents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const [statsRes, talentsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/talents?status=pending`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setStats(statsRes.data);
      setRecentTalents(talentsRes.data.slice(0, 5));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="loading">
          <div className="spinner" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div data-testid="admin-dashboard">
        <h1 style={{ fontSize: '1.75rem', marginBottom: '2rem', fontFamily: 'Playfair Display, serif' }}>
          Dashboard
        </h1>

        {/* Stats Grid */}
        <div className="stats-grid" data-testid="stats-grid">
          <div className="stat-card">
            <div className="stat-label">
              <Users size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
              Total Talents
            </div>
            <div className="stat-value">{stats?.total_talents || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">
              <Clock size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
              Pending Approval
            </div>
            <div className="stat-value warning">{stats?.pending_talents || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">
              <CheckCircle size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
              Approved
            </div>
            <div className="stat-value success">{stats?.approved_talents || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">
              <Mail size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
              Unread Messages
            </div>
            <div className="stat-value primary">{stats?.unread_contacts || 0}</div>
          </div>
        </div>

        {/* Pending Approvals */}
        <div style={{ marginTop: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2 style={{ fontSize: '1.25rem' }}>Pending Approvals</h2>
            <Link to="/admin/talents" className="btn btn-ghost" data-testid="view-all-talents">
              View All
            </Link>
          </div>

          {recentTalents.length === 0 ? (
            <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>
              <CheckCircle size={48} style={{ color: 'var(--success)', marginBottom: '1rem' }} />
              <p style={{ color: 'var(--text-secondary)' }}>No pending approvals!</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Type</th>
                    <th>Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {recentTalents.map((talent) => (
                    <tr key={talent.id} data-testid={`pending-talent-${talent.id}`}>
                      <td data-label="Name">{talent.name}</td>
                      <td data-label="Email">{talent.email}</td>
                      <td data-label="Type">{talent.representation_type || 'N/A'}</td>
                      <td data-label="Submitted">
                        {new Date(talent.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div style={{ marginTop: '2rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <Link to="/admin/talents" className="card" style={{ padding: '1.5rem', textAlign: 'center', textDecoration: 'none' }} data-testid="quick-action-talents">
            <Users size={32} style={{ color: 'var(--primary)', marginBottom: '0.75rem' }} />
            <h4>Manage Talents</h4>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
              Review and manage talent profiles
            </p>
          </Link>
          <Link to="/admin/contacts" className="card" style={{ padding: '1.5rem', textAlign: 'center', textDecoration: 'none' }} data-testid="quick-action-contacts">
            <Mail size={32} style={{ color: 'var(--primary)', marginBottom: '0.75rem' }} />
            <h4>View Messages</h4>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
              Read contact form submissions
            </p>
          </Link>
        </div>
      </div>
    </AdminLayout>
  );
}
